import { useEffect, useState } from 'react';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Checkbox } from 'src/@/components/ui/checkbox';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { RadioGroup, RadioGroupItem } from 'src/@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Separator } from 'src/@/components/ui/separator';
import { Textarea } from 'src/@/components/ui/textarea';
import useAuth from 'src/hooks/useAuth';
import getActionListaUsuariosByClienteId from 'src/actions/usuarios/getActionListaUsuariosByClienteId';
import v2_actionListSectorsByClient from 'src/actions/v2/cliente/v2_actionListSectorsByClient';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const HINT_CLS = 'text-xs text-gray-400';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

// Mantenha em sincronia com lais-ai/src/agents/realestate/identity.js → DEFAULT_AGENT_CONFIG.transferencia
const DEFAULT_GATILHOS = ['cliente_interessado', 'quer_atendente', 'sem_imoveis', 'transfer_broker'];

const GATILHOS = [
    {
        value: 'cliente_interessado',
        title: 'Cliente gostou de um imóvel',
        description: 'Quando o cliente demonstra interesse em um imóvel apresentado (link, código ou sugestão).',
        icon: 'mdi:heart-outline',
    },
    {
        value: 'quer_atendente',
        title: 'Cliente pediu atendente humano',
        description: 'Quando o cliente diz "quero falar com alguém", "tem um corretor?", etc.',
        icon: 'mdi:account-tie-outline',
    },
    {
        value: 'sem_imoveis',
        title: 'Não encontrei imóveis',
        description: 'Quando a busca esgota tentativas de relaxamento sem encontrar opções no perfil pedido.',
        icon: 'mdi:home-search-outline',
    },
    {
        value: 'transfer_broker',
        title: 'Cliente quer mais opções (após N rodadas)',
        description: 'Quando o cliente continua pedindo "outras opções" após o limite de rodadas e a Lais envia o link completo.',
        icon: 'mdi:format-list-bulleted',
    },
];

export default function TransferenciaSection({ aiAgent, changeModuleConfig }) {
    const { user } = useAuth();
    const [setores, setSetores] = useState([]);
    const [usuarios, setUsuarios] = useState([]);

    const transferencia = aiAgent?.config?.module?.realstate?.transferencia || {};
    const modo = transferencia.modo || 'sector';
    const setorId = transferencia.setor_id || '';
    const usuarioId = transferencia.usuario_destino_id || '';
    const gatilhos = Array.isArray(transferencia.gatilhos)
        ? transferencia.gatilhos
        : DEFAULT_GATILHOS;
    const gatilhosCustom = Array.isArray(transferencia.gatilhosCustom)
        ? transferencia.gatilhosCustom
        : [];

    const updateTransferencia = (patch) => {
        changeModuleConfig({
            transferencia: {
                ...transferencia,
                ...patch,
            },
        });
    };

    const addGatilhoCustom = () => {
        const novo = {
            id: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            ativo: true,
            nome: '',
            condicao: '',
        };
        updateTransferencia({ gatilhosCustom: [...gatilhosCustom, novo] });
    };

    const updateGatilhoCustom = (id, patch) => {
        updateTransferencia({
            gatilhosCustom: gatilhosCustom.map((g) => (g.id === id ? { ...g, ...patch } : g)),
        });
    };

    const removeGatilhoCustom = (id) => {
        updateTransferencia({
            gatilhosCustom: gatilhosCustom.filter((g) => g.id !== id),
        });
    };

    useEffect(() => {
        async function load() {
            if (!user?.clienteId) return;
            try {
                const [setoresResp, usuariosResp] = await Promise.all([
                    v2_actionListSectorsByClient(user.clienteId),
                    getActionListaUsuariosByClienteId(user.clienteId),
                ]);
                setSetores(setoresResp?.data?.data || []);
                setUsuarios(usuariosResp?.data?.usuarios || []);
            } catch (error) {
                console.log(error);
            }
        }
        load();
    }, [user?.clienteId]);

    const toggleGatilho = (value, checked) => {
        const set = new Set(gatilhos);
        if (checked) set.add(value);
        else set.delete(value);
        updateTransferencia({ gatilhos: Array.from(set) });
    };

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:account-arrow-right-outline" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>Transferência para Consultor</CardTitle>
                        <CardDescription>Define para quem o chat e o lead serão encaminhados quando a Lais decidir transferir, e em quais cenários isso acontece.</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                    <Label className={LABEL_CLS}>Direcionar para</Label>
                    <RadioGroup
                        value={modo}
                        onValueChange={(v) => updateTransferencia({ modo: v })}
                        className="flex flex-row gap-4"
                    >
                        <div className="flex items-center gap-2">
                            <RadioGroupItem value="sector" id="modo-sector" />
                            <Label htmlFor="modo-sector" className="text-sm cursor-pointer">Setor</Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <RadioGroupItem value="user" id="modo-user" />
                            <Label htmlFor="modo-user" className="text-sm cursor-pointer">Usuário específico</Label>
                        </div>
                    </RadioGroup>

                    {modo === 'sector' && (
                        <div className="flex flex-col gap-2">
                            <Label className={LABEL_CLS}>Setor de destino</Label>
                            <Select
                                value={setorId || undefined}
                                onValueChange={(value) => updateTransferencia({ setor_id: value })}
                            >
                                <SelectTrigger className={INPUT_CLS}>
                                    <SelectValue placeholder={setores.length === 0 ? 'Carregando setores...' : 'Selecione um setor'} />
                                </SelectTrigger>
                                <SelectContent>
                                    {setores.map((s) => (
                                        <SelectItem key={s._id} value={s._id}>{s.nome_setor}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className={HINT_CLS}>O chat será encaminhado para o setor — qualquer corretor disponível atende.</p>
                        </div>
                    )}

                    {modo === 'user' && (
                        <div className="flex flex-col gap-2">
                            <Label className={LABEL_CLS}>Corretor/usuário de destino</Label>
                            <Select
                                value={usuarioId || undefined}
                                onValueChange={(value) => updateTransferencia({ usuario_destino_id: value })}
                            >
                                <SelectTrigger className={INPUT_CLS}>
                                    <SelectValue placeholder={usuarios.length === 0 ? 'Carregando usuários...' : 'Selecione um usuário'} />
                                </SelectTrigger>
                                <SelectContent>
                                    {usuarios.map((u) => (
                                        <SelectItem key={u._id} value={u._id}>{u.nomeFantasia || u.nome || u.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className={HINT_CLS}>O chat e o lead serão atribuídos diretamente a este corretor.</p>
                        </div>
                    )}
                </div>

                <Separator className="bg-gray-200/60" />

                <div className="flex flex-col gap-3">
                    <div>
                        <Label className={LABEL_CLS}>Quando acionar a transferência</Label>
                        <p className="text-xs text-gray-500 mt-1">Marque os cenários em que a Lais deve direcionar a conversa ao consultor humano. Cenários desmarcados continuam recebendo a mensagem da Lais, mas o chat permanece com ela.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {GATILHOS.map((g) => {
                            const ativo = gatilhos.includes(g.value);
                            return (
                                <label
                                    key={g.value}
                                    htmlFor={`gatilho-${g.value}`}
                                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                                        ativo
                                            ? 'border-[#7C3AED] bg-violet-50 shadow-sm shadow-[#7C3AED]/20'
                                            : 'border-gray-200 bg-white hover:border-violet-200 hover:bg-violet-50/40'
                                    }`}
                                >
                                    <Checkbox
                                        id={`gatilho-${g.value}`}
                                        checked={ativo}
                                        onCheckedChange={(checked) => toggleGatilho(g.value, !!checked)}
                                        className="mt-0.5"
                                    />
                                    <div className="flex flex-col gap-1 flex-1">
                                        <div className="flex items-center gap-2">
                                            <Iconify icon={g.icon} width={16} className={ativo ? 'text-[#7C3AED]' : 'text-gray-400'} />
                                            <span className={`text-sm font-semibold ${ativo ? 'text-[#7C3AED]' : 'text-gray-900'}`}>{g.title}</span>
                                        </div>
                                        <p className="text-xs text-gray-500 leading-relaxed">{g.description}</p>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </div>

                <Separator className="bg-gray-200/60" />

                <div className="flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-900">Gatilhos personalizados</h4>
                            <p className="text-xs text-gray-500 mt-1">Crie situações específicas em linguagem natural. Quando a Lais identificar que a mensagem do cliente bate com a condição descrita, o chat é transferido ao consultor.</p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addGatilhoCustom}
                            className="rounded-xl border-gray-200 hover:border-[#7C3AED] hover:text-[#7C3AED] shrink-0"
                        >
                            <Iconify icon="eva:plus-outline" width={16} className="mr-1" />
                            Adicionar
                        </Button>
                    </div>

                    {gatilhosCustom.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            <Iconify icon="mdi:lightbulb-on-outline" width={24} className="text-gray-300" />
                            <p className="text-xs text-gray-500 mt-2">Nenhum gatilho personalizado</p>
                            <p className="text-[11px] text-gray-400 mt-1">Ex.: "cliente menciona financiamento", "cliente pede vistoria", "cliente é investidor"</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {gatilhosCustom.map((g) => {
                                const ativo = g.ativo !== false;
                                return (
                                    <div
                                        key={g.id}
                                        className={`flex flex-col gap-3 p-4 rounded-xl border transition-all ${
                                            ativo
                                                ? 'border-[#7C3AED]/40 bg-violet-50/40'
                                                : 'border-gray-200 bg-white opacity-70'
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <Checkbox
                                                checked={ativo}
                                                onCheckedChange={(checked) => updateGatilhoCustom(g.id, { ativo: !!checked })}
                                                className="mt-2.5"
                                            />
                                            <div className="flex flex-col gap-2 flex-1 min-w-0">
                                                <Input
                                                    value={g.nome || ''}
                                                    onChange={(e) => updateGatilhoCustom(g.id, { nome: e.target.value })}
                                                    placeholder="Nome do gatilho (ex.: Cliente investidor)"
                                                    className={`${INPUT_CLS} font-semibold`}
                                                />
                                                <Textarea
                                                    value={g.condicao || ''}
                                                    onChange={(e) => updateGatilhoCustom(g.id, { condicao: e.target.value })}
                                                    placeholder='Descreva a situação em linguagem natural. Ex.: "Cliente mencionou que é investidor, pretende comprar mais de um imóvel ou está procurando para revenda."'
                                                    className={INPUT_CLS}
                                                    rows={3}
                                                />
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeGatilhoCustom(g.id)}
                                                className="rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                                            >
                                                <Iconify icon="eva:trash-2-outline" width={16} />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
